import { Fragment } from "react";
import { ChevronRight, ChevronsUpDown, ChevronUp, ChevronDown } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import type { Property } from "@/shared/types/client";
import { type PropertySort, type SortColumn } from "@/features/properties/services/propertyFilters";
import { PropertyRowStatus } from "./PropertyRowStatus";
import { DeliverablesProgress } from "./DeliverablesProgress";
import { PropertyInitiativesCell } from "./PropertyInitiativesCell";

interface Props {
  properties: Property[];
  sort: PropertySort;
  onSortChange: (sort: PropertySort) => void;
  onRowClick: (id: string) => void;
}

const SORTABLE: { key: SortColumn; label: string }[] = [
  { key: "name", label: "Store #" },
  { key: "address", label: "Address" },
  // Asset-count sorting is disabled for now — PostgREST can't order a parent by a
  // to-many embedded count, so it never actually sorts. Re-enable once the
  // denormalized count column lands (docs/superpowers/specs/2026-06-22-asset-total-sort-fix.md).
  // { key: "assetCount", label: "Assets" },
];

/** Format a stored date for the Survey/Delivery columns; em dash when absent. */
function formatRowDate(value: unknown): string {
  if (typeof value !== "string" || !value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

export function PropertiesTable({ properties, sort, onSortChange, onRowClick }: Props) {
  const toggle = (key: SortColumn) =>
    onSortChange(sort.column === key ? { column: key, ascending: !sort.ascending } : { column: key, ascending: true });

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {SORTABLE.map(({ key, label }) => (
            <Fragment key={key}>
              <TableHead>
                <button type="button" className="flex items-center gap-1" onClick={() => toggle(key)}>
                  {label}
                  {sort.column === key ? (
                    sort.ascending ? (
                      <ChevronUp className="h-3.5 w-3.5 text-primary" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5 text-primary" />
                    )
                  ) : (
                    <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />
                  )}
                </button>
              </TableHead>
              {key === "address" && <TableHead>Initiatives</TableHead>}
            </Fragment>
          ))}
          <TableHead>Assets</TableHead>
          <TableHead>Progress</TableHead>
          <TableHead>Survey</TableHead>
          <TableHead>Delivery</TableHead>
          <TableHead>Deliverables</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {properties.map((p) => {
          const raw = p as unknown as Record<string, unknown>;
          return (
            <TableRow key={p.id} className="cursor-pointer" onClick={() => onRowClick(p.id)}>
              <TableCell className="py-1.5 font-bold text-foreground">{p.name}</TableCell>
              <TableCell className="py-1.5 text-muted-foreground">{p.address}</TableCell>
              <TableCell className="py-1.5"><PropertyInitiativesCell teams={p.teams} /></TableCell>
              <TableCell className="py-1.5">{p.assetCount ?? 0}</TableCell>
              <TableCell className="py-1.5">
                <PropertyRowStatus status={raw.delivery_status as string | undefined} />
              </TableCell>
              <TableCell className="py-1.5 whitespace-nowrap text-xs text-muted-foreground">
                {formatRowDate(p.lastSurveyDate ?? raw.scan_date)}
              </TableCell>
              <TableCell className="py-1.5 whitespace-nowrap text-xs text-muted-foreground">
                {formatRowDate(raw.delivery_completed_date)}
              </TableCell>
              <TableCell className="py-1.5"><DeliverablesProgress property={raw} /></TableCell>
              <TableCell className="py-1.5 text-muted-foreground"><ChevronRight className="h-4 w-4" /></TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
